// /intake?trip=<id> — the household intake form.
//
// Auto-mounted at /intake by the pages routing convention (see src/App.tsx). One
// scrolling page, six sections, in the order the questions were asked at the
// kitchen table: where you live, how far you'll drive, how you'd travel, when
// you're free, what the house needs, what you want nearby. Every answer saves itself as you go.
//
// A seventh section lets a household name an actual place. It is optional, writes to
// `region` rather than `household`, and stays out of SECTION_ORDER so it never counts
// against completion.
//
// There is now a Save button at the bottom, and it is NOT a gate. The original
// design had none, reasoning that the least technical relative fills this in
// one-handed on a phone and a submit button is one more thing to not press —
// which is still true, so nothing is lost by never pressing it. What it adds is
// the thing per-section badges could not: one place that says out loud that
// everything is in. It also flushes the debounce, so it is the fastest way to be
// certain before closing the tab.
//
// Section (d) is a tap grid on purpose. Issue #21 is a household that answered
// "Check our calendar" in a free-text date box — the grid is what stops that.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { JSX, ReactNode } from 'react'

import { AvailabilityGrid } from '../components/AvailabilityGrid'
import { AuthProvider, RequireAuth, useAuth, useMembership } from '../lib/auth'
import { supabase } from '../lib/supabaseClient'
import type { Household, Mode, Role } from '../lib/types'

import { AddressField } from '../components/intake/AddressField'
import { DriveMaxField } from '../components/intake/DriveMaxField'
import { ModesPicker } from '../components/intake/ModesPicker'
import { RequirementsField } from '../components/intake/RequirementsField'
import { LocationFeaturesField } from '../components/intake/LocationFeaturesField'
import { SuggestPlaceField } from '../components/intake/SuggestPlaceField'

/* ------------------------------------------------------------------ types */

type SectionKey =
  | 'address'
  | 'drive'
  | 'modes'
  | 'availability'
  | 'requirements'
  | 'location'
type SaveState = 'idle' | 'saving' | 'saved' | 'error'

/** The columns this page owns. Everything else on `household` is written elsewhere. */
type Patch = Partial<
  Pick<
    Household,
    | 'origin_town'
    | 'origin_zip'
    | 'origin_lat'
    | 'origin_lng'
    | 'max_drive_hours'
    | 'modes_ok'
    | 'nearest_airports'
    | 'house_requirements_raw'
    | 'house_nice_to_have_raw'
    | 'dates_pending'
    | 'location_must_have_raw'
    | 'location_nice_to_have_raw'
  >
>

interface SectionStatus {
  state: SaveState
  message?: string
}

interface HouseholdOption {
  id: string
  label: string
}

type PageState =
  | { kind: 'loading' }
  | { kind: 'fatal'; message: string }
  | { kind: 'no-household' }
  | { kind: 'ready' }

const SECTION_ORDER: SectionKey[] = [
  'address',
  'drive',
  'modes',
  'availability',
  'requirements',
  'location',
]

const SECTION_TITLES: Record<SectionKey, string> = {
  address: 'Where you live',
  drive: 'Longest drive you would do',
  modes: 'How you would travel',
  availability: 'When you are free',
  requirements: 'What the house needs',
  location: 'Location features',
}

const SECTION_TICKS: Record<SectionKey, string> = {
  address: 'Home',
  drive: 'Drive',
  modes: 'Modes',
  availability: 'Dates',
  requirements: 'House',
  location: 'Nearby',
}

const DEBOUNCE_MS = 800

/* -------------------------------------------------------------- utilities */

function isoDate(d: Date): string {
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Fallback horizon when the trip row has none: this year, Jun 1 – Sep 30. */
function defaultHorizon(): { start: string; end: string } {
  const year = new Date().getFullYear()
  return {
    start: isoDate(new Date(Date.UTC(year, 5, 1))),
    end: isoDate(new Date(Date.UTC(year, 8, 30))),
  }
}

function asIsoDate(v: unknown): string | null {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : null
}

function asMode(v: unknown): v is Mode {
  return v === 'drive' || v === 'rail' || v === 'fly'
}

function asString(v: unknown): string | null {
  return typeof v === 'string' ? v : null
}

function asNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

/**
 * PostgREST hands back `unknown`-shaped rows; narrow into the row type from
 * src/lib/types.ts rather than casting, so a column rename fails here loudly
 * instead of silently rendering blanks.
 */
function normalizeHousehold(row: Record<string, unknown>): Household {
  const modes = Array.isArray(row['modes_ok']) ? (row['modes_ok'] as unknown[]).filter(asMode) : []
  const airports = Array.isArray(row['nearest_airports'])
    ? (row['nearest_airports'] as unknown[]).filter((a): a is string => typeof a === 'string')
    : []
  return {
    id: String(row['id']),
    trip_id: String(row['trip_id'] ?? ''),
    label: asString(row['label']) ?? 'Household',
    // Town + ZIP, never a street address (src/lib/types.ts § Privacy).
    origin_town: asString(row['origin_town']),
    origin_zip: asString(row['origin_zip']),
    origin_lat: asNumber(row['origin_lat']),
    origin_lng: asNumber(row['origin_lng']),
    max_drive_hours: asNumber(row['max_drive_hours']),
    modes_ok: modes,
    nearest_airports: airports,
    house_requirements_raw: asString(row['house_requirements_raw']),
    house_nice_to_have_raw: asString(row['house_nice_to_have_raw']),
    location_must_have_raw: asString(row['location_must_have_raw']),
    location_nice_to_have_raw: asString(row['location_nice_to_have_raw']),
    dates_pending: row['dates_pending'] === true,
    created_at: asString(row['created_at']) ?? '',
  }
}

/* ------------------------------------------------------------ page pieces */

/**
 * `deferred` is a third state between done and not-done: answered with "later".
 * Without it a household that has consciously said "dates to come" reads as an
 * unfinished form forever, which is exactly the nagging this page tries to avoid.
 */
function ProgressBar({
  done,
  deferred,
}: {
  done: Record<SectionKey, boolean>
  deferred: Partial<Record<SectionKey, boolean>>
}) {
  const count = SECTION_ORDER.filter((k) => done[k]).length
  const later = SECTION_ORDER.filter((k) => !done[k] && deferred[k])
  return (
    <header className="spz-progress" aria-label="Form progress">
      <ol className="spz-dots">
        {SECTION_ORDER.map((key, i) => {
          const isLater = !done[key] && Boolean(deferred[key])
          return (
            <li
              key={key}
              className={`spz-dot${done[key] ? ' spz-dot-on' : ''}${isLater ? ' spz-dot-later' : ''}`}
            >
              <span className="spz-dot-mark" aria-hidden="true">
                {done[key] ? '✓' : isLater ? '·' : i + 1}
              </span>
              <span className="spz-dot-label">{SECTION_TICKS[key]}</span>
            </li>
          )
        })}
      </ol>
      <p className="spz-progress-count" aria-live="polite">
        {count} of {SECTION_ORDER.length} done
        {later.length > 0 ? ' · dates to come' : ''}
      </p>
    </header>
  )
}

function SaveBadge({ status, onRetry }: { status: SectionStatus; onRetry: () => void }) {
  if (status.state === 'idle') return null
  if (status.state === 'saving') return <span className="spz-badge">Saving…</span>
  if (status.state === 'saved') return <span className="spz-badge spz-badge-ok">Saved</span>
  return (
    <span className="spz-badge spz-badge-bad">
      Not saved
      <button type="button" className="spz-retry" onClick={onRetry}>
        Retry
      </button>
    </span>
  )
}

function Section({
  index,
  title,
  done,
  status,
  onRetry,
  anchor,
  children,
}: {
  index: number
  title: string
  done: boolean
  status: SectionStatus
  onRetry: () => void
  /**
   * Stable id for deep links, e.g. /intake#requirements. The landing page no
   * longer uses it — it offers one button now — but the anchor is kept: it costs
   * nothing, and #36 (required vs nice-to-have) will want to point straight here.
   */
  anchor?: string
  children: ReactNode
}) {
  return (
    <section className="spz-card" id={anchor} aria-labelledby={`spz-h-${index}`}>
      <div className="spz-card-head">
        <h2 id={`spz-h-${index}`} className="spz-card-title">
          <span className="spz-num" aria-hidden="true">
            {done ? '✓' : index}
          </span>
          {title}
        </h2>
        <SaveBadge status={status} onRetry={onRetry} />
      </div>
      {children}
      {status.state === 'error' && status.message && (
        <p className="spz-error" role="alert">
          {status.message}
        </p>
      )}
    </section>
  )
}

/* -------------------------------------------------------------- the form */

interface RegionCandidate {
  id: string
  name: string
  lat: number
  lng: number
  suggested_by_household_id: string | null
}

function IntakeForm({ tripId }: { tripId: string }): JSX.Element {
  const params = useMemo(() => new URLSearchParams(window.location.search), [])

  // RequireAuth above guarantees a session; this is the membership row behind it.
  const { membership, loading: membershipLoading } = useMembership(tripId)

  const [page, setPage] = useState<PageState>({ kind: 'loading' })
  const [householdId, setHouseholdId] = useState<string | null>(null)
  const [household, setHousehold] = useState<Household | null>(null)
  const [options, setOptions] = useState<HouseholdOption[]>([])
  const [horizon, setHorizon] = useState(defaultHorizon)
  const [availabilityDone, setAvailabilityDone] = useState(false)

  const [status, setStatus] = useState<Record<SectionKey, SectionStatus>>({
    address: { state: 'idle' },
    drive: { state: 'idle' },
    modes: { state: 'idle' },
    availability: { state: 'idle' },
    requirements: { state: 'idle' },
    location: { state: 'idle' },
  })

  // Region candidates for the suggestion section. Every member can read every region
  // (region_select is is_member), which is what makes the list a duplicate check.
  const [candidates, setCandidates] = useState<RegionCandidate[]>([])

  // Reassurance state. The form has always saved itself; what it never did was
  // say so in one place, which is why people asked for a Save button.
  const [dirty, setDirty] = useState(false)
  const [savingAll, setSavingAll] = useState(false)
  const [confirmed, setConfirmed] = useState(false)

  const timers = useRef<Partial<Record<SectionKey, ReturnType<typeof setTimeout>>>>({})
  const pending = useRef<Partial<Record<SectionKey, Patch>>>({})
  const lastFailed = useRef<Partial<Record<SectionKey, Patch>>>({})
  const householdIdRef = useRef<string | null>(null)
  householdIdRef.current = householdId

  const role: Role = membership?.role ?? 'member'
  const readOnly = role === 'viewer'

  /* ---------------------------------------------------------- data load */

  useEffect(() => {
    if (membershipLoading) {
      setPage({ kind: 'loading' })
      return
    }
    if (!membership) {
      setPage({
        kind: 'fatal',
        message: "You're not on this trip yet. Open your invite link, or ask an organizer for one.",
      })
      return
    }

    let cancelled = false

    async function loadHousehold(id: string) {
      const { data: row, error } = await supabase
        .from('household')
        .select('*')
        .eq('id', id)
        .maybeSingle()
      if (cancelled) return

      if (error || !row) {
        setPage({
          kind: 'fatal',
          message: "We couldn't open that household. You may not have access to it.",
        })
        return
      }

      setHousehold(normalizeHousehold(row as Record<string, unknown>))
      setPage({ kind: 'ready' })

      // Completion tick for section (d): any existing availability row counts.
      const { count } = await supabase
        .from('availability')
        .select('id', { count: 'exact', head: true })
        .eq('household_id', id)
      if (!cancelled && (count ?? 0) > 0) setAvailabilityDone(true)
    }

    async function load(trip: string, member: NonNullable<typeof membership>) {
      // The grid's columns come from the trip horizon.
      const { data: tripRow } = await supabase
        .from('trip')
        .select('horizon_start, horizon_end')
        .eq('id', trip)
        .maybeSingle()
      if (cancelled) return
      const row = tripRow as Record<string, unknown> | null
      const start = asIsoDate(row?.['horizon_start'])
      const end = asIsoDate(row?.['horizon_end'])
      if (start && end) setHorizon({ start, end })

      // Organizers may edit any household; everyone else edits their own.
      let targetId: string | null
      if (member.role === 'organizer') {
        const { data: rows } = await supabase
          .from('household')
          .select('id, label')
          .eq('trip_id', trip)
          .order('label')
        if (cancelled) return
        const opts: HouseholdOption[] = ((rows ?? []) as Record<string, unknown>[]).map((r) => ({
          id: String(r['id']),
          label: asString(r['label']) ?? 'Household',
        }))
        setOptions(opts)
        targetId = params.get('household') ?? member.household_id ?? opts[0]?.id ?? null
      } else {
        targetId = member.household_id
      }

      if (!targetId) {
        setPage({ kind: 'no-household' })
        return
      }

      setHouseholdId(targetId)
      await loadHousehold(targetId)
    }

    void load(tripId, membership)
    return () => {
      cancelled = true
    }
  }, [tripId, params, membership, membershipLoading])

  // Organizer switching households.
  const switchHousehold = useCallback(async (id: string) => {
    setHouseholdId(id)
    setHousehold(null)
    setAvailabilityDone(false)
    setPage({ kind: 'loading' })
    const url = new URL(window.location.href)
    url.searchParams.set('household', id)
    window.history.replaceState({}, '', url.toString())

    const { data: row, error } = await supabase
      .from('household')
      .select('*')
      .eq('id', id)
      .maybeSingle()
    if (error || !row) {
      setPage({ kind: 'fatal', message: "We couldn't open that household." })
      return
    }
    setHousehold(normalizeHousehold(row as Record<string, unknown>))
    setPage({ kind: 'ready' })
    const { count } = await supabase
      .from('availability')
      .select('id', { count: 'exact', head: true })
      .eq('household_id', id)
    setAvailabilityDone((count ?? 0) > 0)
  }, [])

  /* -------------------------------------------------------- save engine */

  const flushSection = useCallback(async (section: SectionKey) => {
    const id = householdIdRef.current
    const patch = pending.current[section]
    pending.current[section] = undefined
    if (!id || !patch || Object.keys(patch).length === 0) return

    setStatus((s) => ({ ...s, [section]: { state: 'saving' } }))

    const { data, error } = await supabase
      .from('household')
      .update(patch)
      .eq('id', id)
      .select('id')

    // RLS rejection surfaces either as an error (42501) or as zero rows updated.
    if (error || !data || data.length === 0) {
      lastFailed.current[section] = patch
      setStatus((s) => ({
        ...s,
        [section]: {
          state: 'error',
          message:
            "Couldn't save — you may not have access to this household. Your answer is still here; tap Retry.",
        },
      }))
      return
    }

    lastFailed.current[section] = undefined
    setHousehold((h) => (h ? { ...h, ...patch } : h))
    setStatus((s) => ({ ...s, [section]: { state: 'saved' } }))

    // Clean only when nothing is queued or waiting anywhere — one section
    // finishing while another is mid-debounce is not "all saved".
    if (SECTION_ORDER.every((k) => !pending.current[k] && !timers.current[k])) {
      setDirty(false)
    }
  }, [])

  const loadCandidates = useCallback(async () => {
    const { data } = await supabase
      .from('region')
      .select('id, name, anchor_lat, anchor_lng, suggested_by_household_id')
      .eq('trip_id', tripId)
      .order('name', { ascending: true })
    setCandidates(
      ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
        id: String(r.id),
        name: String(r.name),
        lat: Number(r.anchor_lat),
        lng: Number(r.anchor_lng),
        suggested_by_household_id: (r.suggested_by_household_id as string | null) ?? null,
      })),
    )
  }, [tripId])

  useEffect(() => {
    void loadCandidates()
  }, [loadCandidates])

  const suggestPlace = useCallback(
    async (place: { name: string; lat: number; lng: number }) => {
      const id = householdIdRef.current
      if (!id) throw new Error('no household')
      const { error } = await supabase.from('region').insert({
        trip_id: tripId,
        name: place.name,
        anchor_lat: place.lat,
        anchor_lng: place.lng,
        status: 'proposed',
        suggested_by_household_id: id,
      })
      if (error) throw new Error(error.message)
      await loadCandidates()
    },
    [tripId, loadCandidates],
  )

  const retractPlace = useCallback(
    async (regionId: string) => {
      await supabase.from('region').delete().eq('id', regionId)
      await loadCandidates()
    },
    [loadCandidates],
  )

  const queueSave = useCallback(
    (section: SectionKey, patch: Patch, delay = DEBOUNCE_MS) => {
      pending.current[section] = { ...(pending.current[section] ?? {}), ...patch }
      setDirty(true)
      const existing = timers.current[section]
      if (existing) clearTimeout(existing)
      timers.current[section] = setTimeout(() => {
        timers.current[section] = undefined
        void flushSection(section)
      }, delay)
    },
    [flushSection],
  )

  /**
   * Write everything that is waiting, right now, without waiting out the debounce.
   *
   * Three callers, and they are the whole reason this exists:
   *   - the Save button, so pressing it means something
   *   - the page being hidden or closed, which used to lose the last edit
   *   - unmount, where the old cleanup cleared the timers and dropped the patch
   */
  const flushAll = useCallback(async () => {
    await Promise.all(
      SECTION_ORDER.map(async (key) => {
        const timer = timers.current[key]
        if (timer) {
          clearTimeout(timer)
          timers.current[key] = undefined
        }
        // No-ops when that section has nothing pending.
        await flushSection(key)
      }),
    )
  }, [flushSection])

  /** The Save button: flush everything, then say so. */
  const saveNow = useCallback(async () => {
    setSavingAll(true)
    await flushAll()
    setSavingAll(false)
    setConfirmed(true)
  }, [flushAll])

  const retry = useCallback(
    (section: SectionKey) => {
      const patch = lastFailed.current[section]
      if (!patch) return
      queueSave(section, patch, 0)
    },
    [queueSave],
  )

  // Flush anything in flight when the page goes away.
  //
  // This used to clearTimeout every pending save and drop the patch on the floor,
  // which is the opposite of what the comment claimed. Typing a house requirement
  // and closing the tab inside the 800ms debounce lost it silently, with the badge
  // still reading "Saved" from the previous flush.
  //
  // `pagehide` and `visibilitychange` cover the cases React never sees: closing
  // the tab, switching apps on a phone, locking the screen. Unmount covers
  // navigating away inside the SPA. A request started here may be cut off if the
  // browser tears the page down immediately, so this shortens the window rather
  // than closing it — which is why the Save button exists too.
  useEffect(() => {
    const onHide = (): void => {
      if (document.visibilityState === 'hidden') void flushAll()
    }
    const onPageHide = (): void => void flushAll()

    document.addEventListener('visibilitychange', onHide)
    window.addEventListener('pagehide', onPageHide)

    return () => {
      document.removeEventListener('visibilitychange', onHide)
      window.removeEventListener('pagehide', onPageHide)
      void flushAll()
    }
  }, [flushAll])

  /* ------------------------------------------------------------ render */

  if (page.kind === 'loading') {
    return (
      <>
        <IntakeStyles />
        <main className="spz-shell">
          <p className="spz-hint">
            <span className="spz-spinner" aria-hidden="true" /> Loading your form…
          </p>
        </main>
      </>
    )
  }

  if (page.kind === 'fatal') {
    return (
      <>
        <IntakeStyles />
        <main className="spz-shell">
          <h1 className="spz-title">Household intake</h1>
          <p className="spz-error" role="alert">
            {page.message}
          </p>
        </main>
      </>
    )
  }

  if (page.kind === 'no-household' || !household || !householdId) {
    return (
      <>
        <IntakeStyles />
        <main className="spz-shell">
          <h1 className="spz-title">Household intake</h1>
          <p className="spz-error" role="alert">
            You haven&rsquo;t been assigned to a household yet — ask an organizer.
          </p>
        </main>
      </>
    )
  }

  // Any section that failed to write. The Save button must not claim success
  // while a section is sitting in an error state.
  const anyError = SECTION_ORDER.some((k) => status[k].state === 'error')

  const done: Record<SectionKey, boolean> = {
    address: household.origin_lat !== null && household.origin_lng !== null,
    drive: household.max_drive_hours !== null,
    modes: household.modes_ok.length > 0,
    availability: availabilityDone,
    requirements:
      (household.house_requirements_raw ?? '').trim().length > 0 ||
      (household.house_nice_to_have_raw ?? '').trim().length > 0,
    location:
      (household.location_must_have_raw ?? '').trim().length > 0 ||
      (household.location_nice_to_have_raw ?? '').trim().length > 0,
  }

  return (
    <>
      <IntakeStyles />
      <main className="spz-shell">
        <ProgressBar done={done} deferred={{ availability: household.dates_pending }} />

        <h1 className="spz-title">Your household</h1>
        <p className="spz-sub">
          You&rsquo;re filling in for <strong>{household.label}</strong>. Six quick questions so
          we can find dates and a house that work for everyone. Answers save themselves — there
          is no submit button.
        </p>

        {role === 'organizer' && options.length > 0 && (
          <label className="spz-field spz-picker">
            <span className="spz-label">Editing as organizer</span>
            <select
              className="spz-input"
              value={householdId}
              onChange={(e) => void switchHousehold(e.target.value)}
            >
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        )}

        {readOnly && <p className="spz-hint spz-readonly">View only — you can look, not change.</p>}

        {/* (a) home — town + ZIP, never a street address */}
        <Section
          index={1}
          title={SECTION_TITLES.address}
          done={done.address}
          status={status.address}
          onRetry={() => retry('address')}
        >
          <AddressField
            town={household.origin_town}
            zip={household.origin_zip}
            lat={household.origin_lat}
            lng={household.origin_lng}
            disabled={readOnly}
            onConfirm={(value) => queueSave('address', value, 0)}
          />
        </Section>

        {/* (b) max drive hours */}
        <Section
          index={2}
          title={SECTION_TITLES.drive}
          done={done.drive}
          status={status.drive}
          onRetry={() => retry('drive')}
        >
          <DriveMaxField
            value={household.max_drive_hours}
            disabled={readOnly}
            onChange={(hours) => queueSave('drive', { max_drive_hours: hours })}
          />
        </Section>

        {/* (c) travel modes + airports */}
        <Section
          index={3}
          title={SECTION_TITLES.modes}
          done={done.modes}
          status={status.modes}
          onRetry={() => retry('modes')}
        >
          <ModesPicker
            modes={household.modes_ok}
            airports={household.nearest_airports}
            disabled={readOnly}
            onModesChange={(modes) => queueSave('modes', { modes_ok: modes }, 0)}
            onAirportsChange={(airports) => queueSave('modes', { nearest_airports: airports }, 0)}
          />
        </Section>

        {/* (d) availability — the grid owns its own loading and saving. */}
        <Section
          index={4}
          title={SECTION_TITLES.availability}
          done={done.availability}
          status={status.availability}
          onRetry={() => retry('availability')}
        >
          <p className="spz-hint">
            Tap a week to cycle it: preferred, possible, blackout. Press and hold a week to set
            single days.
          </p>
          <AvailabilityGrid
            tripId={tripId}
            householdId={householdId}
            horizonStart={horizon.start}
            horizonEnd={horizon.end}
            readOnly={readOnly}
            onSaved={() => {
              setAvailabilityDone(true)
              // Real dates supersede "we'll tell you later". Leaving the flag set
              // on a household that has since answered would misreport them to the
              // organizer as still owing dates.
              if (household.dates_pending) {
                queueSave('availability', { dates_pending: false }, 0)
              }
            }}
          />

          {/* The escape hatch. Everything else on this form can be answered today;
              dates often genuinely cannot — a school calendar or a shift rota isn't
              out yet. Without this, such a household is indistinguishable from one
              that never opened the link. */}
          {!availabilityDone && (
            <label className="spz-later">
              <input
                type="checkbox"
                checked={household.dates_pending}
                disabled={readOnly}
                onChange={(e) =>
                  queueSave('availability', { dates_pending: e.target.checked }, 0)
                }
              />
              <span>
                <strong>We don&rsquo;t know our dates yet.</strong>
                <br />
                <span className="spz-hint">
                  Everything else you&rsquo;ve filled in still counts. We&rsquo;ll ask you again
                  rather than guess — the trip needs two days when every household overlaps, so
                  yours are the one answer nobody else can give.
                </span>
              </span>
            </label>
          )}
        </Section>

        {/* (e) house requirements — raw text only */}
        <Section
          index={5}
          title={SECTION_TITLES.requirements}
          done={done.requirements}
          status={status.requirements}
          onRetry={() => retry('requirements')}
          anchor="requirements"
        >
          <RequirementsField
            mustHave={household.house_requirements_raw}
            niceToHave={household.house_nice_to_have_raw}
            disabled={readOnly}
            onMustHaveChange={(value) =>
              queueSave('requirements', { house_requirements_raw: value })
            }
            onNiceToHaveChange={(value) =>
              queueSave('requirements', { house_nice_to_have_raw: value })
            }
          />
        </Section>

        {/* (f) location features — what you want NEAR the house, not in it. A
            lake is a property of where the house is, so this steers the region
            stage rather than the house stage. */}
        <Section
          index={6}
          title={SECTION_TITLES.location}
          done={done.location}
          status={status.location}
          onRetry={() => retry('location')}
          anchor="location"
        >
          <LocationFeaturesField
            mustHave={household.location_must_have_raw}
            niceToHave={household.location_nice_to_have_raw}
            disabled={readOnly}
            onMustHaveChange={(value) =>
              queueSave('location', { location_must_have_raw: value })
            }
            onNiceToHaveChange={(value) =>
              queueSave('location', { location_nice_to_have_raw: value })
            }
          />
        </Section>

        {/* (g) a named place, as opposed to (f)'s "what you want nearby". Optional, so it
            is deliberately outside SECTION_ORDER and never counts against completion. */}
        <Section
          index={7}
          title="Somewhere you have in mind"
          done={candidates.some((c) => c.suggested_by_household_id === householdId)}
          status={{ state: 'idle' }}
          onRetry={() => {}}
          anchor="suggest"
        >
          <SuggestPlaceField
            candidates={candidates}
            ownIds={candidates
              .filter((c) => c.suggested_by_household_id === householdId)
              .map((c) => c.id)}
            disabled={readOnly}
            onSuggest={suggestPlace}
            onRetract={retractPlace}
          />
        </Section>

        {/* The nearest thing this form has to a submit confirmation. There is no
            submit button by design, so completion has to be stated in words or
            nobody knows they can stop. */}
        {/* The Save button.
            Autosave stays exactly as it was, and this is deliberately NOT a gate:
            nothing is lost by never pressing it, which is what the original
            no-submit-button design was protecting. What it adds is the one thing
            per-section badges could not — a single place that says, out loud,
            that everything is in. It also flushes the debounce, so it is the
            fastest way to be certain before closing the tab. */}
        <div className="spz-save">
          <button
            type="button"
            className="spz-btn spz-btn-primary"
            onClick={() => void saveNow()}
            disabled={savingAll || readOnly}
          >
            {savingAll ? 'Saving…' : dirty ? 'Save now' : 'Saved'}
          </button>

          <p className="spz-hint spz-foot" aria-live="polite">
            {savingAll
              ? 'Saving your answers…'
              : dirty
                ? 'You have changes that haven’t been written yet.'
                : confirmed
                  ? anyError
                    ? 'Some answers didn’t save — check the sections marked above.'
                    : done.address &&
                        done.drive &&
                        done.modes &&
                        household.dates_pending &&
                        !done.availability
                      ? 'All saved. That’s everything we need for now — we’ll come back to you about dates.'
                      : 'All saved. You can close the page; come back any time to change an answer.'
                  : 'Everything above saves on its own as you go. Press Save if you’d rather see it confirmed.'}
          </p>
        </div>

        {/* Intake used to end here with nowhere to go, so nobody ever saw what their
            answers produced. */}
        <nav className="spz-onward">
          <h2>See where it&rsquo;s heading</h2>
          <p className="spz-hint">
            Both update as more households fill this in, so they&rsquo;ll change.
          </p>
          <a className="spz-btn" href={`/windows?trip=${encodeURIComponent(tripId)}`}>
            Dates that work for everyone
          </a>
          <a className="spz-btn" href={`/regions?trip=${encodeURIComponent(tripId)}`}>
            Places being considered
          </a>
        </nav>
      </main>
    </>
  )
}

/* --------------------------------------------------------------- the gate */

/**
 * Works out which trip this person is filling in for.
 *
 * `/intake` used to hard-require `?trip=<id>` and dead-end on "this link is
 * missing its trip" without it. That made the page reachable only from an invite
 * link, which is why the landing page pointed at a GitHub issue template instead.
 * Almost everyone belongs to exactly one trip, so look it up: the param still
 * wins when present, one membership resolves silently, and only genuine ambiguity
 * asks a question.
 *
 * `membership` is readable through RLS for trips you belong to, so this needs no
 * edge function.
 */
type TripGateState =
  | { kind: 'loading' }
  | { kind: 'ready'; tripId: string }
  | { kind: 'none' }
  | { kind: 'choose'; trips: { id: string; name: string }[] }
  | { kind: 'error'; message: string }

function TripGate(): JSX.Element {
  const { user } = useAuth()
  const userId = user?.id ?? null
  const paramTrip = useMemo(
    () => new URLSearchParams(window.location.search).get('trip'),
    [],
  )
  const [state, setState] = useState<TripGateState>(
    paramTrip ? { kind: 'ready', tripId: paramTrip } : { kind: 'loading' },
  )

  useEffect(() => {
    if (paramTrip || !userId) return
    let cancelled = false

    void (async () => {
      const { data: rows, error } = await supabase
        .from('membership')
        .select('trip_id')
        .eq('user_id', userId)
      if (cancelled) return
      if (error) {
        setState({ kind: 'error', message: 'We could not look up your trips.' })
        return
      }

      const ids = [...new Set((rows ?? []).map((r) => (r as { trip_id: string }).trip_id))]
      if (ids.length === 0) {
        setState({ kind: 'none' })
        return
      }
      const first = ids[0]
      if (ids.length === 1 && first) {
        setState({ kind: 'ready', tripId: first })
        return
      }

      // More than one trip — name them so the choice means something.
      const { data: trips } = await supabase.from('trip').select('id, name').in('id', ids)
      if (cancelled) return
      setState({
        kind: 'choose',
        trips: ((trips ?? []) as { id: string; name: string }[]).map((t) => ({
          id: t.id,
          name: t.name,
        })),
      })
    })()

    return () => {
      cancelled = true
    }
  }, [paramTrip, userId])

  if (state.kind === 'loading') {
    return (
      <>
        <IntakeStyles />
        <main className="spz-shell">
          <p className="spz-hint">
            <span className="spz-spinner" aria-hidden="true" /> Finding your trip…
          </p>
        </main>
      </>
    )
  }

  if (state.kind === 'none') {
    return (
      <>
        <IntakeStyles />
        <main className="spz-shell">
          <h1 className="spz-title">Household intake</h1>
          <p className="spz-sub">
            You&rsquo;re signed in, but you&rsquo;re not on a trip yet. If an organizer has put
            your address on the list, your invitation is already waiting &mdash; there is no code
            to type.
          </p>
          <p>
            <a className="spz-btn spz-btn-primary" href="/join">
              Find my invitation
            </a>
          </p>
        </main>
      </>
    )
  }

  if (state.kind === 'error') {
    return (
      <>
        <IntakeStyles />
        <main className="spz-shell">
          <h1 className="spz-title">Household intake</h1>
          <p className="spz-error" role="alert">
            {state.message}
          </p>
        </main>
      </>
    )
  }

  if (state.kind === 'choose') {
    return (
      <>
        <IntakeStyles />
        <main className="spz-shell">
          <h1 className="spz-title">Which trip?</h1>
          <p className="spz-sub">You&rsquo;re on more than one. Pick the one you&rsquo;re filling in for.</p>
          {state.trips.map((t) => (
            <p key={t.id}>
              <a className="spz-btn" href={`/intake?trip=${encodeURIComponent(t.id)}`}>
                {t.name}
              </a>
            </p>
          ))}
        </main>
      </>
    )
  }

  return <IntakeForm tripId={state.tripId} />
}

/* -------------------------------------------------------------- the page */

export function IntakePage(): JSX.Element {
  return (
    <AuthProvider>
      <RequireAuth>
        <TripGate />
      </RequireAuth>
    </AuthProvider>
  )
}

export default IntakePage

/* --------------------------------------------------------------- styles */
/* Scoped to `spz-` class names and kept inside this module: src/index.css
   belongs to spz-00-scaffold and no other module may edit it. */

function IntakeStyles() {
  return <style>{CSS}</style>
}

const CSS = `
.spz-shell { max-width: 34rem; margin: 0 auto; padding: 0 1rem 4rem; display: flex; flex-direction: column; gap: 1rem; }
.spz-title { font-size: 1.5rem; letter-spacing: -0.02em; margin-top: 0.5rem; }
.spz-sub { color: var(--muted); font-size: 0.95rem; }
.spz-foot { text-align: center; }
/* The save row. Sticky on a phone so the confirmation is reachable without
   scrolling back down a five-section form. */
.spz-save { display: flex; flex-direction: column; gap: 0.5rem; align-items: stretch; margin-top: 0.5rem; }
.spz-save .spz-btn { min-height: 3rem; font-size: 1rem; }
.spz-readonly { font-weight: 600; }

.spz-onward { display: flex; flex-direction: column; gap: 0.5rem; margin-top: 2rem; padding-top: 1.25rem; border-top: 1px solid var(--line); }
.spz-onward h2 { font-size: 1.05rem; margin: 0; }
.spz-onward .spz-btn { min-height: 3rem; text-align: center; text-decoration: none; display: flex; align-items: center; justify-content: center; }

.spz-candidates { margin-top: 1rem; }
.spz-candidates ul { list-style: none; padding: 0; margin: 0.35rem 0 0; display: flex; flex-direction: column; gap: 0.3rem; }
.spz-candidates li { display: flex; align-items: center; justify-content: space-between; gap: 0.6rem; font-size: 0.9rem; padding: 0.3rem 0; border-bottom: 1px dashed var(--line); }
.spz-candidates .spz-more { min-height: 2.5rem; margin-top: 0.5rem; padding: 0.3rem 0.8rem; font-size: 0.8rem; font-weight: 600; background: transparent; color: var(--accent); border: 1px solid var(--line); border-radius: 999px; }
.spz-retract { min-height: 2rem; padding: 0.15rem 0.55rem; font-size: 0.75rem; background: transparent; color: var(--muted); border: 1px solid var(--line); border-radius: 0.4rem; flex: none; }
.spz-confirm { margin-top: 0.75rem; padding: 0.75rem; border: 2px solid var(--accent); border-radius: 0.6rem; display: flex; flex-direction: column; gap: 0.5rem; }

.spz-progress { position: sticky; top: 0; z-index: 5; background: var(--ground); padding: 0.6rem 0 0.5rem; border-bottom: 1px solid rgba(127,127,127,0.25); }
.spz-dots { display: flex; gap: 0.25rem; list-style: none; padding: 0; margin: 0; }
.spz-dot { flex: 1 1 0; min-width: 0; display: flex; flex-direction: column; align-items: center; gap: 0.15rem; color: var(--muted); }
.spz-dot-mark { width: 1.6rem; height: 1.6rem; border-radius: 50%; display: grid; place-items: center; font-size: 0.8rem; font-weight: 700; border: 2px solid currentColor; }
.spz-dot-label { font-size: 0.6rem; letter-spacing: 0.02em; text-transform: uppercase; }
.spz-dot-on { color: var(--accent); }
.spz-dot-on .spz-dot-mark { background: var(--accent); color: var(--ground); border-color: var(--accent); }
/* Answered with "later": settled, not outstanding — so a dashed ring rather than
   a numbered one, and never the accent, which means done. */
.spz-dot-later .spz-dot-mark { border-style: dashed; }

/* The "we don't know our dates yet" opt-out. */
.spz-later { display: flex; gap: 0.6rem; align-items: flex-start; margin-top: 0.85rem; padding: 0.75rem; border: 1px dashed var(--line); border-radius: 0.6rem; }
.spz-later input { flex: none; width: 1.15rem; height: 1.15rem; min-height: 0; margin-top: 0.15rem; accent-color: var(--accent); }
.spz-later .spz-hint { display: block; margin-top: 0.15rem; }
.spz-progress-count { font-size: 0.7rem; color: var(--muted); text-align: center; margin-top: 0.3rem; }

.spz-card { background: rgba(127,127,127,0.08); border: 1px solid rgba(127,127,127,0.2); border-radius: 0.9rem; padding: 0.9rem; display: flex; flex-direction: column; gap: 0.6rem; }
.spz-card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 0.5rem; }
.spz-card-title { font-size: 1.02rem; line-height: 1.3; display: flex; align-items: center; gap: 0.5rem; }
.spz-num { flex: 0 0 auto; width: 1.5rem; height: 1.5rem; border-radius: 50%; background: var(--accent); color: var(--ground); display: grid; place-items: center; font-size: 0.8rem; font-weight: 700; }

.spz-badge { font-size: 0.7rem; color: var(--muted); white-space: nowrap; }
.spz-badge-ok { color: var(--accent); }
.spz-badge-bad { color: #b3261e; display: inline-flex; align-items: center; gap: 0.3rem; }
.spz-retry { font: inherit; background: none; border: 1px solid currentColor; border-radius: 0.4rem; padding: 0.1rem 0.35rem; color: inherit; }

.spz-field { display: flex; flex-direction: column; gap: 0.3rem; }
.spz-grow { flex: 1 1 auto; min-width: 0; }
.spz-zip { flex: 0 0 6.5rem; }
.spz-picker { margin-bottom: 0.25rem; }
.spz-label { font-size: 0.78rem; color: var(--muted); text-transform: uppercase; letter-spacing: 0.04em; }
.spz-sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
.spz-input, .spz-textarea { font: inherit; width: 100%; min-height: 44px; padding: 0.55rem 0.7rem; border-radius: 0.6rem; border: 1px solid rgba(127,127,127,0.45); background: var(--ground); color: var(--ink); }
.spz-textarea { min-height: 7rem; resize: vertical; line-height: 1.5; }
.spz-input:focus-visible, .spz-textarea:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }
.spz-airports { margin-top: 0.7rem; }

.spz-row { display: flex; gap: 0.5rem; align-items: flex-end; }
.spz-row-tight { align-items: center; gap: 0.5rem; }

.spz-btn { font: inherit; min-height: 44px; padding: 0.5rem 0.9rem; border-radius: 0.6rem; border: 1px solid var(--accent); background: transparent; color: var(--accent); font-weight: 600; }
.spz-btn:disabled { opacity: 0.45; }
.spz-btn-primary { background: var(--accent); color: var(--ground); }
.spz-btn-quiet { border-color: rgba(127,127,127,0.5); color: var(--ink); }

.spz-confirmed { font-weight: 600; }
.spz-match { border: 1px dashed var(--accent); border-radius: 0.6rem; padding: 0.6rem; display: flex; flex-direction: column; gap: 0.5rem; margin-top: 0.6rem; }
.spz-match-text { font-weight: 600; }
.spz-hint { font-size: 0.78rem; color: var(--muted); }
.spz-warn { font-size: 0.78rem; color: #8a6100; }
.spz-error { font-size: 0.85rem; color: #b3261e; }
.spz-muted { color: var(--muted); }

.spz-readout { font-size: 1.6rem; font-weight: 700; letter-spacing: -0.02em; }
.spz-step { font: inherit; flex: 0 0 auto; width: 48px; height: 48px; font-size: 1.4rem; line-height: 1; border-radius: 0.6rem; border: 1px solid var(--accent); background: transparent; color: var(--accent); }
.spz-step:disabled { opacity: 0.4; }
.spz-range { flex: 1 1 auto; min-width: 0; height: 44px; accent-color: var(--accent); }

.spz-chips { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.spz-chip { font: inherit; flex: 1 1 6rem; min-height: 48px; border-radius: 999px; border: 1px solid rgba(127,127,127,0.5); background: transparent; color: var(--ink); font-weight: 600; }
.spz-chip-on { background: var(--accent); border-color: var(--accent); color: var(--ground); }

.spz-spinner { display: inline-block; width: 0.8em; height: 0.8em; border: 2px solid currentColor; border-right-color: transparent; border-radius: 50%; animation: spz-spin 0.7s linear infinite; vertical-align: -0.05em; }
@keyframes spz-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .spz-spinner { animation: none; } }
`
